import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../services/api';

// Spinner Component
const Spinner = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  return (
    <svg
      className={`animate-spin ${sizeClasses[size]}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
};

// Alert Component
const Alert = ({ variant = 'info', children, className = '' }) => {
  const variants = {
    danger: 'bg-red-50 text-red-800 border-red-200',
    success: 'bg-green-50 text-green-800 border-green-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200',
    warning: 'bg-yellow-50 text-yellow-800 border-yellow-200'
  };

  return (
    <div className={`border rounded-lg p-4 ${variants[variant]} ${className}`}>
      {children}
    </div>
  );
};

const PaymentMethodForm = ({
  onSuccess,
  onCancel,
  isLoading = false,
  showCancelButton = true,
  buttonText = "Add Payment Method",
  autoFocus = true
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [cardholderName, setCardholderName] = useState('');
  const [cardError, setCardError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [setupIntentLoading, setSetupIntentLoading] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeInitError, setStripeInitError] = useState(false);
  const [cardFormReady, setCardFormReady] = useState(false);

  // Card element styling options
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true,
  };

  // Check if Stripe is ready
  useEffect(() => {
    if (stripe && elements) {
      setStripeReady(true);
      setStripeInitError(false);
    } else {
      setStripeReady(false);

      // Set a timeout to detect if Stripe fails to initialize
      const timeoutId = setTimeout(() => {
        if (!stripe || !elements) {
          console.error('Stripe initialization timeout');
          setStripeInitError(true);
        }
      }, 5000); // 5 second timeout

      return () => clearTimeout(timeoutId);
    }
  }, [stripe, elements]);

  // Fetch setup intent when component mounts
  useEffect(() => {
    if (stripeReady) {
      fetchSetupIntent();
    }
  }, [stripeReady]);

  const fetchSetupIntent = async () => {
    if (clientSecret || !stripeReady) return; // Already have a client secret or Stripe not ready

    setSetupIntentLoading(true);
    try {
      const response = await api.createSetupIntent();

      if (response.data && response.data.client_secret) {
        setClientSecret(response.data.client_secret);
        setCardError('');
        setCardFormReady(true);
      } else if (response.data && response.data.msg) {
        // Special handling for authentication errors - don't show as blocking errors
        if (response.data.msg.includes('Authentication error')) {
          setClientSecret('dummy_secret_for_unauthenticated_flow');
          setCardFormReady(true);
        } else {
          console.error('Setup intent error:', response.data.msg);
          setCardError(`Payment setup error: ${response.data.msg}`);
          setCardFormReady(false);
        }
      } else {
        console.error('Unexpected setup intent response:', response.data);
        setCardError('Failed to initialize payment form. Please try again.');
        setCardFormReady(false);
      }
    } catch (error) {
      console.error('Error creating setup intent:', error);
      setCardError('Failed to initialize payment form. Please try again.');
      setCardFormReady(false);
    } finally {
      setSetupIntentLoading(false);
    }
  };

  // Track card element changes to enable/disable the submit button
  const handleCardChange = (event) => {
    if (event.complete && !event.error) {
      setCardFormReady(true);
    } else {
      setCardFormReady(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripeReady) {
      setCardError('Payment system is not ready. Please try again in a moment.');
      return;
    }

    if (!cardholderName.trim()) {
      setCardError('Please enter the cardholder name.');
      return;
    }

    setProcessing(true);
    setCardError('');

    try {
      // Get the card element
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        setCardError('Payment form not properly loaded. Please refresh and try again.');
        setProcessing(false);
        return;
      }

      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardholderName.trim(),
        },
      });

      if (error) {
        console.error('Error creating payment method:', error);
        setCardError(error.message);
        setProcessing(false);
        return;
      }

      // Validate and save the payment method via backend
      const validationResponse = await api.validateAndSavePaymentMethod({
        payment_method_id: paymentMethod.id,
        cardholder_name: cardholderName.trim(),
        save_card: true
      });

      if (validationResponse.data.success) {
        if (onSuccess) {
          onSuccess(paymentMethod.id);
        }
      } else {
        console.error('Payment method validation failed:', validationResponse.data.error);
        setCardError(validationResponse.data.error || 'Failed to validate payment method');
      }
    } catch (error) {
      console.error('Error in payment method submission:', error);
      setCardError('An error occurred while processing your payment method. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Show loading state while Stripe is initializing
  if (!stripeReady && !stripeInitError) {
    return (
      <div className="p-4 text-center">
        <div className="flex justify-center mb-3">
          <Spinner size="md" />
        </div>
        <p>Initializing payment system...</p>
      </div>
    );
  }

  // Show error if Stripe failed to initialize
  if (stripeInitError) {
    return (
      <Alert variant="danger">
        <div className="mb-2">
          <h4 className="font-semibold text-lg">Payment System Error</h4>
        </div>
        <p className="mb-2">
          There was a problem initializing the payment system. This could be due to:
        </p>
        <ul className="list-disc ml-5 mb-2">
          <li>Network connectivity issues</li>
          <li>Ad blockers or privacy extensions</li>
          <li>Missing or invalid API keys</li>
        </ul>
        <p className="mb-4">
          Please try refreshing the page or disabling any content blockers.
          If the problem persists, contact customer support.
        </p>
        <div className="flex justify-between gap-2 mt-4">
          {showCancelButton && (
            <button
              onClick={onCancel}
              className="border border-gray-300 text-gray-700 px-4 py-2 rounded-full hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-full hover:bg-blue-700 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Cardholder Name
        </label>
        <input
          type="text"
          placeholder="Name on card"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          autoFocus={autoFocus}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Information
        </label>
        <div className="p-3 border border-gray-300 rounded-lg">
          {setupIntentLoading ? (
            <div className="text-center py-2 flex items-center justify-center">
              <Spinner size="sm" />
              <span className="ml-2">Preparing payment form...</span>
            </div>
          ) : (
            <CardElement options={cardElementOptions} onChange={handleCardChange} />
          )}
        </div>
        {cardError && (
          <div className="text-red-600 mt-2 text-sm">
            {cardError}
          </div>
        )}
      </div>

      <div className="flex justify-between gap-2 mt-4">
        {showCancelButton && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading || processing}
            className="border border-gray-300 text-gray-700 px-4 py-2 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isLoading || processing || setupIntentLoading || !cardFormReady || !cardholderName.trim()}
          className={`bg-blue-600 text-white px-4 py-2 rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 ${showCancelButton ? '' : 'ml-auto'}`}
        >
          {processing ? (
            <span className="flex items-center">
              <Spinner size="sm" />
              <span className="ml-2">Processing...</span>
            </span>
          ) : (
            buttonText
          )}
        </button>
      </div>
    </form>
  );
};

export default PaymentMethodForm;
